import {
  ApolloClient,
  ApolloProvider,
  from,
  HttpLink,
  InMemoryCache,
  Operation,
  ServerError,
  ServerParseError,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { RetryLink } from '@apollo/client/link/retry';
import { useMonitoringClient } from '@xometry/ui';
import { Modal, notification } from 'antd';
import { defaultDataIdFromObject } from 'apollo-cache-inmemory';
import { AccessErrorNotificationApiHolder } from 'components/shared/AccessError/AccessErrorNotificationApiHolder';
import { useAccessErrorNotification } from 'components/shared/AccessError/useAccessErrorNotification';
import { Maintenance } from 'components/shared/Maintenance';
import { NotFound } from 'components/shared/NotFound';
import { ENV_API_ENDPOINT } from 'constants/env';
import { HTTP_STATUS } from 'constants/httpStatus';
// eslint-disable-next-line no-restricted-imports
import { get } from 'es-toolkit/compat';
import stringify from 'fast-safe-stringify';
import { GraphQLError, Kind, OperationDefinitionNode } from 'graphql';
import React, { FC, ReactNode, useEffect, useMemo } from 'react';
import { useStore } from 'stores/RootStore';
import { AddressFragment } from 'utils/graphql/fragments/__generated__/address';
import { MessengerDealSubscriptionFragmentFragment } from 'utils/graphql/fragments/__generated__/messengerDealSubscriptionFragment';
import { getAuthToken } from 'utils/graphql/requests';
import { handleUnauthorizedError } from 'utils/unauthorizedUtils';

interface Props {
  children: ReactNode;
}

export const GQLProvider: FC<Props> = (props) => {
  const { children } = props;
  const monitoringClient = useMonitoringClient();
  const accessErrorNotification = useAccessErrorNotification();

  const client = useMemo(() => {
    const cache = new InMemoryCache({
      addTypename: true,
      dataIdFromObject: (object) => {
        switch (object.__typename) {
          case 'MessageSource': {
            const o = object as MessengerDealSubscriptionFragmentFragment;

            return `MessageSource:${o.sourceType || ''}:${o.sourceId || ''}`;
          }

          case 'DealShippingAddressType': {
            const keysForJoin: Array<keyof AddressFragment> = [
              'address',
              'city',
              'company',
              'country',
              'id',
              'name',
              'phone',
              'zip',
            ];
            const dealShippingAddress = object as AddressFragment;
            const key = keysForJoin.map((k) => dealShippingAddress[k] || '').join('_');

            return `DealShippingAddressType:${key}`;
          }

          default:
            return defaultDataIdFromObject(object) || undefined;
        }
      },
    });

    const networkErrorHandler = (operation: Operation, networkError: ServerError | ServerParseError) => {
      const statusCode = networkError.statusCode;

      if (statusCode === HTTP_STATUS.UNAUTHORIZED) {
        handleUnauthorizedError();

        return;
      }

      if (statusCode === HTTP_STATUS.FORBIDDEN) {
        // Show error only on unauthorized mutation
        // For authorization errors in query, notification should be
        // shown only when it's manually requested
        const operationData = String(get(operation, 'query.definitions.0.operation'));

        if (operationData === 'mutation' || operationData === 'query') {
          const error = String(get(networkError, 'result.error'));

          accessErrorNotification(error);
        }

        return;
      }

      if (statusCode === HTTP_STATUS.NOT_FOUND) {
        const operationName = operation.operationName;
        const url = networkError.response.url;
        const variables = stringify(operation.variables);

        Modal.info({
          title: 'Not found',
          content: NotFound({
            operationName,
            url,
            variables,
          }),
          width: 800,
        });

        return;
      }

      switch (statusCode) {
        case HTTP_STATUS.INTERNAL_SERVER_ERROR:
          notification.error({ message: 'Something went wrong!' });

          break;
        case HTTP_STATUS.SERVICE_UNAVAILABLE:
          Modal.info({
            title: 'Attention',
            content: Maintenance,
            width: 800,
          });

          break;
        default: {
          notification.error({ message: 'Error', description: networkError.toString() });
        }
      }

      monitoringClient.captureError(networkError, {
        level: 'warning',
        extras: {
          networkError: stringify(networkError),
        },
      });
    };

    const graphqlErrorHandler = (rawErrors: readonly GraphQLError[]) => {
      rawErrors.forEach((rawError) => {
        notification.error({ message: 'Error', description: rawError.message });

        try {
          const message =
            rawError?.message && Array.isArray(rawError?.path)
              ? `[${rawError.path.join('/')}]: ${rawError.message}`
              : rawError?.originalError?.message;

          // Do not log login attempt with incorrect password
          if (message && message.includes('usersEmailLogin') && message.includes('Account not found')) {
            return;
          }

          monitoringClient.captureError(message ? new Error(message) : rawError, {
            level: 'warning',
            extras: {
              rawError: stringify(rawError),
            },
          });
        } catch (e: unknown) {
          monitoringClient.captureError(e instanceof Error ? e : new Error(String(e)), {
            level: 'warning',
            extras: {
              customError: e,
              rawError: stringify(rawError),
            },
          });
        }
      });
    };

    // Add exponential retry if thre is a network error occured
    // e.g. user lost connection or backend is redeploying.
    const retryLink = new RetryLink({
      delay: {
        initial: 2000,
        jitter: true,
        max: 60000,
      },
      attempts: {
        max: 6,
        retryIf: (error, operation) => {
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
          const operationDefinition = operation.query.definitions.find((d) => d.kind === Kind.OPERATION_DEFINITION) as
            | OperationDefinitionNode
            | undefined;

          if (operationDefinition?.operation === 'mutation') return false;

          const statusCode = (error as { statusCode?: number })?.statusCode;

          if (typeof statusCode === 'number' && !isNaN(statusCode)) {
            return statusCode >= 500;
          }

          return Boolean(error);
        },
      },
    });

    const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
      const { ignoreErrors = [], handleErrorsLocally = false } = operation.getContext();

      if (graphQLErrors && !handleErrorsLocally) {
        const reportedErrors = graphQLErrors.filter((e) => !ignoreErrors.includes(e.message));

        if (reportedErrors.length) {
          console.error(`graphql errors [${operation.operationName}]: `, reportedErrors);
          graphqlErrorHandler(reportedErrors);
        }
      }

      if (networkError && 'statusCode' in networkError) {
        console.error(`graphql network error [${operation.operationName}]: `, networkError);
        networkErrorHandler(operation, networkError);
      }
    });
    const httpLink = new HttpLink({
      uri: (operation) => `${ENV_API_ENDPOINT}/oms/graphql?${operation.operationName}`,
    });
    const authLink = setContext((_, { headers }) => {
      const token = getAuthToken();

      return {
        // Why it is typed as any, apollo?
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        headers: {
          ...headers,
          accept: 'application/json',
          authorization: token,
        },
        credentials: 'include',
      };
    });

    const link = from([retryLink, errorLink, authLink.concat(httpLink)]);

    return new ApolloClient({ cache, link, connectToDevTools: process.env.NODE_ENV === 'development' });
  }, [accessErrorNotification, monitoringClient]);

  const store = useStore();

  useEffect(() => {
    store.setGraphlQLClient(client);
  }, [client, store]);

  return (
    <ApolloProvider client={client}>
      <AccessErrorNotificationApiHolder>{children}</AccessErrorNotificationApiHolder>
    </ApolloProvider>
  );
};
